// TS port of sport-templates/lib/color-separation.js (byte-copied server-side into
// ../../../lib/color-separation.js). The browser cannot import the CJS lib, so this
// file re-implements it and color-separation.contract.test.ts pins the two together.
// Keep the maths identical: CIE76 ΔE*ab, default floor 20, white-primary window 8.

export const ROLES = ['primary', 'secondary', 'tertiary'] as const;
export type Role = (typeof ROLES)[number];
export const DEFAULT_MIN_DELTA_E = 20;
export const WHITE_DELTA_E = 8;
const HEX6 = /^#[0-9A-F]{6}$/;

export type SeparationError = { code: 'MISSING_COLOR' | 'INVALID_HEX' | 'WHITE_PRIMARY' | 'COLORS_TOO_CLOSE'; field?: Role; pair?: [Role, Role]; deltaE?: number; message: string };
export type PaletteEntry = { id?: string; name?: string; hex?: string; color?: string };
export type Snapped = { id: string; name: string; hex: string | null; deltaE: number } | null;

export function normalizeHex(input: unknown): string | null {
  if (input == null) return null;
  let s = String(input).trim();
  if (!s) return null;
  if (s[0] !== '#') s = '#' + s;
  if (/^#[0-9a-fA-F]{3}$/.test(s)) s = '#' + s[1] + s[1] + s[2] + s[2] + s[3] + s[3];
  s = s.toUpperCase();
  return HEX6.test(s) ? s : null;
}

function hexToRgb(hex: unknown): { r: number; g: number; b: number } | null {
  const h = normalizeHex(hex);
  if (!h) return null;
  return { r: parseInt(h.slice(1, 3), 16), g: parseInt(h.slice(3, 5), 16), b: parseInt(h.slice(5, 7), 16) };
}

function rgbToLab({ r, g, b }: { r: number; g: number; b: number }): { L: number; a: number; b: number } {
  const lin = (v: number) => { v /= 255; return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  const R = lin(r), G = lin(g), B = lin(b);
  const x = (R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047;
  const y = (R * 0.2126 + G * 0.7152 + B * 0.0722) / 1.0;
  const z = (R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883;
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const fx = f(x), fy = f(y), fz = f(z);
  return { L: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
}

export function deltaE76(hexA: unknown, hexB: unknown): number {
  const a = hexToRgb(hexA), b = hexToRgb(hexB);
  if (!a || !b) return Infinity;
  const la = rgbToLab(a), lb = rgbToLab(b);
  return Math.sqrt((la.L - lb.L) ** 2 + (la.a - lb.a) ** 2 + (la.b - lb.b) ** 2);
}

export function colorSeparationError(
  colors: Partial<Record<Role, unknown>> | null | undefined,
  { minDeltaE = DEFAULT_MIN_DELTA_E, requireAll = true }: { minDeltaE?: number; requireAll?: boolean } = {},
): SeparationError | null {
  const c = colors || {};
  const hex: Partial<Record<Role, string>> = {};
  for (const role of ROLES) {
    const raw = c[role];
    const present = raw != null && String(raw).trim() !== '';
    if (!present) {
      if (role === 'primary' || requireAll) return { code: 'MISSING_COLOR', field: role, message: `${role} colour is required.` };
      continue;
    }
    const h = normalizeHex(raw);
    if (!h) return { code: 'INVALID_HEX', field: role, message: `${role} colour must be a hex colour like #0033A0.` };
    hex[role] = h;
  }
  if (deltaE76(hex.primary, '#FFFFFF') < WHITE_DELTA_E) {
    return { code: 'WHITE_PRIMARY', field: 'primary', message: "Primary colour cannot be white — white is the negative space, pick the school's main ink." };
  }
  const roles = ROLES.filter((r) => hex[r]);
  for (let i = 0; i < roles.length; i++) {
    for (let j = i + 1; j < roles.length; j++) {
      const d = deltaE76(hex[roles[i]], hex[roles[j]]);
      if (d < minDeltaE) {
        return {
          code: 'COLORS_TOO_CLOSE', pair: [roles[i], roles[j]], deltaE: Math.round(d * 10) / 10,
          message: `${roles[i]} (${hex[roles[i]]}) and ${roles[j]} (${hex[roles[j]]}) are too close to tell apart (ΔE ${Math.round(d)}, need ${minDeltaE}). Pick two clearly different colours.`,
        };
      }
    }
  }
  return null;
}

export function snapToPalette(hexes: unknown[], palettes: PaletteEntry[] | null | undefined): Snapped[] {
  const taken = new Set<string>();
  return (hexes || []).map((raw) => {
    const h = normalizeHex(raw);
    if (!h) return null;
    let best: Snapped = null;
    for (const p of palettes || []) {
      if (!p || !p.id || taken.has(p.id)) continue;
      const d = deltaE76(h, p.hex || p.color);
      if (!best || d < best.deltaE) best = { id: p.id, name: p.name || p.id, hex: normalizeHex(p.hex || p.color), deltaE: Math.round(d * 10) / 10 };
    }
    if (best) taken.add(best.id);
    return best;
  });
}
